import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Loader2, X, Calendar as CalIcon, Save } from "lucide-react";
import { toast } from "sonner";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import apiClient from "./api";
import { SERVICES } from "../data/mock";

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM",
  "5:00 PM", "6:00 PM", "7:00 PM",
];

const inputCls =
  "w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none transition-all";

export default function NewBookingModal({ open, onClose, onCreated, defaults }) {
  const [form, setForm] = useState({
    name: defaults?.name || "",
    phone: defaults?.phone || "",
    email: defaults?.email || "",
    pet_name: defaults?.pet_name || "",
    pet_type: defaults?.pet_type || "Dog",
    pet_breed: defaults?.pet_breed || "",
    service: defaults?.service || "",
    time_slot: defaults?.time_slot || "",
    location: defaults?.location || "Studio",
    message: defaults?.message || "",
    offer_code: defaults?.offer_code || "",
  });
  const [offer, setOffer] = useState(null);
  const [date, setDate] = useState();
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const code = form.offer_code.trim().toUpperCase();
  useEffect(() => {
    if (code.length < 9) { setOffer(null); return; }
    const t = setTimeout(() => {
      apiClient.get("/api/admin/offers/lookup", { params: { code } })
        .then((r) => setOffer(r.data))
        .catch(() => setOffer({ status: "notfound" }));
    }, 300);
    return () => clearTimeout(t);
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service || !date || !form.time_slot) {
      toast.error("Please fill name, phone, service, date and time slot.");
      return;
    }
    if (code && offer?.status !== "active") {
      toast.error("Offer code is not valid — clear it or enter an active code.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, date: format(date, "yyyy-MM-dd") };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      payload.name = form.name;
      payload.phone = form.phone;
      payload.service = form.service;
      payload.time_slot = form.time_slot;
      payload.date = format(date, "yyyy-MM-dd");
      const { data } = await apiClient.post("/api/admin/bookings", payload);
      toast.success(data.offer_code ? `Booking added · ${data.offer_code} redeemed` : "Booking added");
      onCreated && onCreated(data);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not save booking");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="new-booking-modal"
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0d0d0d] border border-white/10 rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0d0d0d]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/50">New entry</p>
            <h2 className="font-heading text-lg font-bold text-white">Add booking / lead</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="new-booking-close"
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Customer name *</label>
              <input
                data-testid="nb-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className={inputCls}
                placeholder="e.g. Priya Sharma"
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Phone *</label>
              <input
                data-testid="nb-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className={inputCls}
                placeholder="10-digit mobile"
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Email</label>
              <input
                data-testid="nb-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className={inputCls}
                placeholder="optional"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Pet name</label>
              <input
                data-testid="nb-pet-name"
                value={form.pet_name}
                onChange={(e) => update("pet_name", e.target.value)}
                className={inputCls}
                placeholder="e.g. Bruno"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Pet type</label>
              <Select value={form.pet_type} onValueChange={(v) => update("pet_type", v)}>
                <SelectTrigger data-testid="nb-pet-type" className="bg-black/50 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  <SelectItem value="Dog">Dog</SelectItem>
                  <SelectItem value="Cat">Cat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Breed</label>
              <input
                data-testid="nb-breed"
                value={form.pet_breed}
                onChange={(e) => update("pet_breed", e.target.value)}
                className={inputCls}
                placeholder="e.g. Labrador"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Service *</label>
              <Select value={form.service} onValueChange={(v) => update("service", v)}>
                <SelectTrigger data-testid="nb-service" className="bg-black/50 border-white/10 text-white">
                  <SelectValue placeholder="Choose service" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                  ))}
                  <SelectItem value="Full Grooming">Full Grooming</SelectItem>
                  <SelectItem value="Other / Custom">Other / Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Date *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="nb-date-trigger"
                    className={`${inputCls} flex items-center justify-between text-left ${date ? "" : "text-white/40"}`}
                  >
                    {date ? format(date, "PPP") : "Pick a date"}
                    <CalIcon className="w-4 h-4 text-white/50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                    className="text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Time slot *</label>
              <Select value={form.time_slot} onValueChange={(v) => update("time_slot", v)}>
                <SelectTrigger data-testid="nb-time" className="bg-black/50 border-white/10 text-white">
                  <SelectValue placeholder="Pick a time" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white max-h-60">
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Location</label>
              <div className="grid grid-cols-2 gap-2">
                {["Studio", "Home"].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    data-testid={`nb-loc-${opt.toLowerCase()}`}
                    onClick={() => update("location", opt)}
                    className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition-all ${
                      form.location === opt
                        ? "border-[#FC5106] bg-[#FC5106]/10 text-[#FC5106]"
                        : "border-white/10 bg-black/30 text-white/70 hover:border-white/20"
                    }`}
                  >
                    {opt === "Home" ? "Home (doorstep)" : "At studio"}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Offer code</label>
              <input
                data-testid="nb-offer-code"
                value={form.offer_code}
                onChange={(e) => update("offer_code", e.target.value.toUpperCase())}
                className={`${inputCls} font-mono tracking-wider`}
                placeholder="e.g. BNF-7KQ2M (optional)"
              />
              <OfferCodeStatus code={code} offer={offer} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Notes / customer message</label>
              <textarea
                data-testid="nb-notes"
                rows={3}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className={`${inputCls} resize-none`}
                placeholder="Any special request, allergies, source, etc."
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="nb-cancel"
              className="px-4 py-2.5 text-sm font-semibold text-white/70 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="nb-submit"
              className="btn-primary !py-2.5 !px-5 !text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save booking</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OfferCodeStatus({ code, offer }) {
  if (!code) return null;
  if (code.length < 9) return <p className="mt-1.5 text-xs text-white/40" data-testid="nb-offer-status">Enter the full code (BNF-XXXXX)</p>;
  if (!offer) return <p className="mt-1.5 text-xs text-white/40" data-testid="nb-offer-status">Checking code…</p>;
  const until = offer.expires_at ? format(new Date(offer.expires_at), "d MMM yyyy") : "";
  const map = {
    active: ["text-green-400", `Valid · ${offer.offer_title} · ${offer.name} · until ${until}. Will be marked redeemed on save.`],
    redeemed: ["text-red-400", "This code has already been redeemed."],
    expired: ["text-red-400", `This code expired on ${until}.`],
    notfound: ["text-red-400", "Code not found."],
  };
  const [cls, text] = map[offer.status];
  return <p className={`mt-1.5 text-xs ${cls}`} data-testid="nb-offer-status">{text}</p>;
}
